import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect, useCallback, useMemo } from "react";
import { ArrowLeft, CirclePlus, Search, Loader2, Pen, Trash2, X, Save } from "lucide-react";
import toast from "react-hot-toast";
import { categoriasApi, type CategoryApiResponse } from '@/features/almacen/api/herramientas.api';
import { usePermissions, ALMACEN_PERMISSIONS } from '@/shared/hooks/usePermissions';
import { useFilaSaliente } from "@/shared/hooks/useFilaSaliente";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

// ─── Shared input styles ──────────────────────────────────────────────────────

const INPUT =
  "w-full px-3 py-2 border-0 rounded-md bg-[#F6F9FB] text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 transition-colors";

// ─── Form modal (crear / editar) ──────────────────────────────────────────────

interface FormData {
  name: string;
  description: string;
}

const CategoriaFormModal = ({
  mode,
  initial,
  onClose,
  onSubmit,
}: {
  mode: "create" | "edit";
  initial?: FormData;
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<void>;
}) => {
  const [form, setForm] = useState<FormData>(initial ?? { name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const set =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isValid = form.name.trim() !== "";

  const handleSubmit = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await onSubmit(form);
      onClose();
    } catch (err: unknown) {
      const e = err as { statusCode?: number; message?: string | string[] };
      const msg =
        e.statusCode === 500
          ? "Error del servidor. Inténtalo de nuevo."
          : Array.isArray(e.message)
          ? e.message.join(" ")
          : e.message ?? "Error al guardar";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CapaDeModal>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {mode === "create" ? "Registrar nueva categoría" : "Editar categoría"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="h-6 w-6 bg-[#D9D9D9] rounded-full flex items-center justify-center hover:bg-[#D9D9D9]/60 transition-colors"
          >
            <X className="w-3.5 h-3.5 text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={set("name")}
              placeholder="Ej. Eléctrica"
              className={INPUT}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={form.description}
              onChange={set("description")}
              rows={4}
              placeholder="Descripción corta de la categoría"
              className={`${INPUT} resize-none`}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={estiloDeBoton("secundario")}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || saving}
            className={estiloDeBoton("primario")}
          >
            <Save className="w-4 h-4" />
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </CapaDeModal>
  );
};

// ─── Main list view ───────────────────────────────────────────────────────────

const CategoriasListView = ({ onBack }: { onBack: () => void }) => {
  const { hasPermission } = usePermissions();
  const canCrear    = hasPermission(ALMACEN_PERMISSIONS.HERRAMIENTAS_CREAR);
  const canEditar   = hasPermission(ALMACEN_PERMISSIONS.HERRAMIENTAS_EDITAR);
  const canEliminar = hasPermission(ALMACEN_PERMISSIONS.HERRAMIENTAS_ELIMINAR);

  const [categorias, setCategorias] = useState<CategoryApiResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState<{ mode: "create" | "edit"; data?: CategoryApiResponse } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CategoryApiResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { claseDeFila, animarSalida, cancelarSalida } = useFilaSaliente();

  const fetchCategorias = useCallback(async () => {
    setLoading(true);
    try {
      const data = await categoriasApi.findAll();
      const list = Array.isArray(data) ? data : [];
      // Más recientes primero
      list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setCategorias(list);
    } catch {
      setCategorias([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategorias(); }, [fetchCategorias]);

  const rows = useMemo(() => {
    const q = search.toLowerCase();
    return categorias.filter(
      (c) =>
        !q ||
        c.name.toLowerCase().includes(q) ||
        (c.description ?? "").toLowerCase().includes(q),
    );
  }, [categorias, search]);

  const handleCreate = async (data: FormData) => {
    await categoriasApi.create({ name: data.name.trim(), description: data.description.trim() || undefined });
    toast.success("Categoría creada.");
    fetchCategorias();
  };

  const handleUpdate = async (id: string, data: FormData) => {
    await categoriasApi.update(id, { name: data.name.trim(), description: data.description.trim() || undefined });
    toast.success("Categoría actualizada.");
    fetchCategorias();
  };

  const handleDelete = async () => {
    if (!confirmDelete || deleting) return;
    setDeleting(true);
    try {
      await categoriasApi.remove(confirmDelete.id);
      toast.success("Categoría eliminada.");
      setConfirmDelete(null);
      // A-16: la fila sale antes de releer el listado.
      await animarSalida(confirmDelete.id);
      fetchCategorias();
    } catch (err: unknown) {
      cancelarSalida(confirmDelete.id);
      const e = err as { statusCode?: number; message?: string | string[] };
      const msg =
        e.statusCode === 409
          ? "No se puede eliminar: hay herramientas que usan esta categoría."
          : Array.isArray(e.message)
          ? e.message.join(" ")
          : e.message ?? "Error al eliminar";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className={estiloDeBoton("secundario")}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <TituloDePantalla nivel="interna">Categorías de herramientas</TituloDePantalla>
        </div>
        {canCrear && (
          <button
            type="button"
            onClick={() => setModal({ mode: "create" })}
            className={estiloDeBoton("primario")}
          >
            <CirclePlus className="w-4 h-4" />
            Agregar categoría
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en categorías..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
          />
        </div>
        <button
          onClick={() => setSearch("")}
          className="text-sm font-semibold text-[#315FB9] hover:underline"
        >
          Limpiar
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Cargando categorías…</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ backgroundColor: "#FAF9FF", borderBottom: "1px solid #E4E4E4" }}>
                  {["Nombre", "Descripción", "Acciones"].map((h) => (
                    <th
                      key={h}
                      className="text-left py-3 px-4 text-sm font-semibold first:pl-6 last:pr-6 last:text-right whitespace-nowrap"
                      style={{ color: "#534D59" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <div className="flex flex-col items-center justify-center py-16 gap-2">
                        <p className="text-sm font-medium text-gray-500">Sin resultados</p>
                        <p className="text-sm text-gray-400">
                          {categorias.length === 0
                            ? "Aún no hay categorías. Crea la primera."
                            : "Intenta con otra búsqueda."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.id}
                      className={`hover:bg-gray-50/50 transition-colors ${claseDeFila(row.id)}`}
                      style={{ borderBottom: "1px solid #E4E4E4" }}
                    >
                      <td className="py-4 px-4 pl-6 text-gray-700 font-medium whitespace-nowrap">{row.name}</td>
                      <td className="py-4 px-4 text-gray-500 max-w-sm truncate">
                        {row.description || <span className="italic text-gray-300">Sin descripción</span>}
                      </td>
                      <td className="py-4 px-4 pr-6 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          {canEditar && (
                            <button
                              type="button"
                              onClick={() => setModal({ mode: "edit", data: row })}
                              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                              title="Editar"
                            >
                              <Pen className="h-4 w-4" style={{ color: "#315FB9" }} />
                            </button>
                          )}
                          {canEliminar && (
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(row)}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                          {!canEditar && !canEliminar && (
                            <span className="text-gray-300 text-sm">Sin acciones</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {modal && (
        <CategoriaFormModal
          mode={modal.mode}
          initial={modal.data ? { name: modal.data.name, description: modal.data.description ?? "" } : undefined}
          onClose={() => setModal(null)}
          onSubmit={(data) =>
            modal.mode === "create" ? handleCreate(data) : handleUpdate(modal.data!.id, data)
          }
        />
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <CapaDeModal>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">¿Eliminar categoría?</h3>
            <p className="text-sm text-gray-500 mb-5">
              Esta acción eliminará{" "}
              <span className="font-medium text-gray-700">"{confirmDelete.name}"</span>. No se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className={estiloDeBoton("secundario")}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className={estiloDeBoton("peligro")}
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {deleting ? "Eliminando…" : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </div>
  );
};

export default CategoriasListView;
